// The purpose of this class is to maintain
import { doneInQueueStore, makeUniqJobId, startInQueueStore, updateProgressInQueueStore, } from "./QueueStore";
import { messagesApi } from "@/Api/Messages";
/**
 * The parent class for all queues. This class is not meant to be used directly.
 * It is meant to be extended by a child class that implements the runJobBatch()
 * method.
 */
export class QueueParent {
    _maxTotalProcs;
    _procsPerJobBatch;
    // Each batch of inputs runs per job. Inputs are batched because it might be
    // useful to not create and destroy workers for every single job. So it
    // happens per batch, not per job. But note that batchSize is 1 by default.
    _inputBatches = [];
    // This is just for reporting. Not actually used for anything beyond that.
    _jobsCurrentlyRunning = {};
    // The outputs of each job are stored here. Not batched.
    _outputs = [];
    // A timer that checks job status every so often to add new jobs to the
    // queue if appropriate.
    _queueTimer;
    // How many processors are being currently used by jobs in the queue.
    _numProcsCurrentlyRunning = 0;
    _callbacks;
    // The total number of jobs
    _numTotalJobs;
    // An id unique to this queue.
    _id;
    _showInQueue;
    // If this is true, job will be cancelled as soon as possible.
    jobsCancelling;
    // The done promise is resolved when the queue is finished. Same as the
    // callback, but promised based if that's your preference.
    done;
    _doneResolveFunc;
    _onJobAfterQueueDone;
    /**
     * Whether the queue has been requested to cancel. Subclasses can poll this
     * during long-running batches (e.g. WASM modules that don't naturally
     * yield) to abort in-flight work and clean up resources promptly.
     *
     * @returns {boolean} True if cancellation has been requested.
     */
    get isCancelling() {
        return this.jobsCancelling;
    }
    /**
     * The class constructor.
     *
     * @param {string|undefined} jobTypeId              A string that identifies
     *                                                  the type of job.
     * @param {any[]}            inputs                 An flat array of inputs
     *                                                  to be processed.
     * @param {number}           maxProcs               The maximum number of
     *                                                  processors that can be
     *                                                  used by the queue.
     * @param {IQueueCallbacks}  [callbacks]            The callbacks to be used
     *                                                  by the queue, if any.
     *                                                  Default is undefined.
     * @param {number}           [procsPerJobBatch]     The number of processors
     *                                                  that can be used by each
     *                                                  batch of jobs. Default
     *                                                  is 1.
     * @param {number}           [simulBatches]         The max number of
     *                                                  batches to run
     *                                                  simultaneously. If
     *                                                  undefined, calculated as
     *                                                  maxProcs /
     *                                                  procsPerJobBatch (to run
     *                                                  as many batches at same
     *                                                  time as possible).
     *                                                  Default is undefined.
     * @param {number}           [batchSize]            The number of jobs per
     *                                                  batch. If undefined,
     *                                                  calculated as
     *                                                  inputs.length /
     *                                                  simulBatches. Default is
     *                                                  undefined.
     * @param {boolean}          [showInQueue]          Whether to show this job
     *                                                  in the queue. Default is
     *                                                  true.
     * @param {boolean}          [onJobAfterQueueDone]  Whether to run the
     *                                                  onJobDone callback after
     *                                                  the queue is done. Set
     *                                                  to false if you want to
     *                                                  run the onJobDone
     *                                                  callback yourself.
     *                                                  Default is true.
     */
    constructor(jobTypeId, inputs, maxProcs, callbacks, procsPerJobBatch = 1, simulBatches, batchSize = undefined, showInQueue = true, onJobAfterQueueDone = true) {
        this._numTotalJobs = inputs.length;
        this._procsPerJobBatch = procsPerJobBatch;
        this._callbacks = callbacks;
        this.jobsCancelling = false;
        this._showInQueue = showInQueue;
        this._onJobAfterQueueDone = onJobAfterQueueDone;
        // Adjust max number of processors to be used by the queue if necessary.
        // Useful if there are very few items in the queue.
        this._maxTotalProcs = maxProcs;
        this._maxTotalProcs = Math.min(this._maxTotalProcs, this._numTotalJobs * this._procsPerJobBatch);
        if (simulBatches === undefined) {
            simulBatches = Math.floor(this._maxTotalProcs / this._procsPerJobBatch);
        }
        this._maxTotalProcs = Math.min(this._maxTotalProcs, simulBatches * this._procsPerJobBatch);
        // this._maxTotalProcs -= this._maxTotalProcs % batchSize;
        if (batchSize === undefined) {
            // If batch size isn't defined, make it big enough to use all the
            // processors. Letting the user define this specifically in case
            // they want to use the onJobDone callback to do something with the
            // outputs as they become available, instead of using the
            // onQueueDone callback (when everything done).
            batchSize = Math.ceil(inputs.length / simulBatches);
        }
        this._id = makeUniqJobId(jobTypeId);
        this.done = new Promise((resolve) => {
            this._doneResolveFunc = resolve;
        });
        if (inputs.length === 0) {
            // If the queue is empty, just be done with it.
            this._onQueueDone([]);
            return;
        }
        this._onQueueStart();
        // Copy inputs into IJobInfo[]. Use map.
        const inputInfos = inputs.map((input, index) => {
            return {
                index,
                input: input,
            };
        });
        // Split the inputs into batches. Keep in mind that the size of inputs
        // may not be exactly divisible by batchSize.
        for (let i = 0; i < inputInfos.length; i += batchSize) {
            this._inputBatches.push(inputInfos.slice(i, i + batchSize));
        }
        // this._reportQueueStatusForDebug();
        this._queueTimer = setInterval(() => {
            // If there are no jobs left in _inputBatches, stop the timer.
            if (this._inputBatches.length === 0) {
                clearInterval(this._queueTimer);
                return;
            }
            if (this.jobsCancelling) {
                // Note that this._queueTimer is not cleared here. It is cleared
                // elsewhere.
                return;
            }
            this._fillQueueToCapacity();
        }, 250);
    }
    /**
     * A function that shows the status of the queue in the console. This is
     * only for debugging.
     */
    _reportQueueStatusForDebug() {
        let lastNumJobsFinished = 0;
        setInterval(() => {
            // How many jobs are in this._inputBatches, noting that it is a list of
            // lists?
            let numJobsNotStarted = 0;
            for (const batch of this._inputBatches) {
                numJobsNotStarted += batch.length;
            }
            const jobsRunning = Object.keys(this._jobsCurrentlyRunning).length;
            const numJobsFinished = this._outputs.length;
            if (numJobsFinished === lastNumJobsFinished) {
                // No change since last time. Don't report.
                return;
            }
            lastNumJobsFinished = numJobsFinished;
            // clear console
            // console.clear();
            console.log("Jobs not yet started:", numJobsNotStarted);
            console.log("Jobs running:", jobsRunning);
            console.log("Jobs finished:", numJobsFinished);
        }, 100);
    }
    /**
     * A function that fills the queue to capacity with new running jobs. This
     * function is called every so often by a timer.
     */
    _fillQueueToCapacity() {
        while (true) {
            // Cancel in progress
            if (this.jobsCancelling) {
                return;
            }
            // No more input batches to add.
            if (this._inputBatches.length === 0) {
                break;
            }
            if (this._numProcsCurrentlyRunning + this._procsPerJobBatch >
                this._maxTotalProcs) {
                // Adding job wouldn't fit in the queue.
                break;
            }
            const inputBatch = this._inputBatches.shift();
            // No more input batches to add.
            if (!inputBatch) {
                break;
            }
            // Add jobs to the _jobsCurrentlyRunning list.
            for (const jobInfo of inputBatch) {
                this._jobsCurrentlyRunning[jobInfo.index] = jobInfo;
            }
            this._numProcsCurrentlyRunning += this._procsPerJobBatch;
            this.runJobBatch(inputBatch, this._procsPerJobBatch)
                .then((outBatch) => {
                this._outputs.push(...outBatch);
                this._numProcsCurrentlyRunning -= this._procsPerJobBatch;
                // Remove jobs from the _jobsCurrentlyRunning list
                for (const jobInfo of inputBatch) {
                    delete this._jobsCurrentlyRunning[jobInfo.index];
                }
                // Call the onJobDone callback for each job in the batch.
                if (this._onJobAfterQueueDone) {
                    for (const jobInfo of inputBatch) {
                        this._onJobDone(jobInfo);
                    }
                }
                // Call the onProgress callback.
                this._onProgress(this._outputs.length / this._numTotalJobs);
                // Check if there are no jobs left.
                if (Object.keys(this._jobsCurrentlyRunning).length === 0 &&
                    this._inputBatches.length === 0) {
                    // No jobs left. Call the onQueueDone callback.
                    this._onQueueDone(this._outputs);
                }
                return;
            })
                .catch((err) => {
                // throw err;
                // TODO: Never gets here. Why?
                console.error("Error running job:", err);
                this._numProcsCurrentlyRunning -= this._procsPerJobBatch;
                // Call the onError callback for each job in the batch.
                this._onError(inputBatch, err);
                let msg = err.message;
                if (msg.indexOf("SharedArrayBuffer") !== -1) {
                    msg += ". Consider updating your Safari browser or using a different browser such as Google Chrome.";
                }
                if (msg.indexOf("must not be shared") !== -1) {
                    msg += ". This browser (likely Safari) does not support using SharedArrayBuffer with TextDecoder. We recommend using Google Chrome on Desktop for docking.";
                }
                messagesApi.popupError(msg);
                throw new Error(msg);
            });
        }
    }
    /**
     * The onQueueStart callback to call when the queue starts.
     */
    _onQueueStart() {
        if (this._showInQueue) {
            startInQueueStore(this._id, this._maxTotalProcs, () => {
                // This function allows the queue to be cancelled from an external
                // location.
                // To the extent possible, abort currently running jobs.
                this.jobsCancelling = true;
                // Stop the timer that will try to submit additional jobs
                clearInterval(this._queueTimer);
            });
        }
    }
    /**
     * The onJobDone callback to called when a single job is done.
     *
     * @param {IJobInfo} jobInfo  The job info of the job that is done.
     */
    _onJobDone(jobInfo) {
        if (this._callbacks && this._callbacks.onJobDone) {
            this._callbacks.onJobDone(jobInfo.output, jobInfo.index);
        }
    }
    /**
     * The onError callback to called when a batch of jobs fails.
     *
     * @param {IJobInfo[]} jobInfos  The job infos of the failed batch.
     * @param {any}        error     The error.
     */
    _onError(jobInfos, error) {
        const payloadsOfBatchThatFailed = jobInfos.map((jobInfo) => {
            return jobInfo.input;
        });
        if (this._callbacks && this._callbacks.onError) {
            this._callbacks.onError(payloadsOfBatchThatFailed, error);
        }
    }
    /**
     * The onQueueDone callback to called when the queue (all jobs) is done.
     *
     * @param {IJobInfo[]} outputJobs  The output jobs.
     */
    _onQueueDone(outputJobs) {
        // Sort the output jobs by their original index.
        outputJobs.sort((a, b) => a.index - b.index);
        // Get the payloads of the outputs.
        const outputPayloads = outputJobs.map((jobInfo) => jobInfo.output);
        if (this._showInQueue) {
            doneInQueueStore(this._id);
        }
        if (this._callbacks && this._callbacks.onQueueDone) {
            this._callbacks.onQueueDone(outputPayloads);
        }
        // Also resolve the promise, in case promise is being used instead of
        // callbacks.
        this._doneResolveFunc(outputPayloads);
    }
    /**
     * The onProgress callback to update the progress.
     *
     * @param {number} percent  The percent of jobs that have been completed.
     */
    _onProgress(percent) {
        if (this._showInQueue) {
            updateProgressInQueueStore(this._id, percent);
        }
        if (this._callbacks && this._callbacks.onProgress) {
            this._callbacks.onProgress(percent);
        }
    }
}
